import React, { useEffect, useMemo, useState } from "react";
import { AiOutlineFullscreenExit } from "react-icons/ai";
import Participant from "./Participant";
import { User } from "models/User";
import { useSelector } from "react-redux";
import { RootState } from "store";
import ParticipantExpanded from "./ParticipantExpanded";
import useMediaQuery from "hooks/useMediaQuery";
import { BsPersonFillSlash } from "react-icons/bs";

interface Props {
  view?: any;
  tableDetails: any;
}

export const ExpandedNoParticipantPanel = ({ name,style,aspectRatio }: any) => {

  const getTailwindSize = (size: number | string, isWidth: boolean): string => {
    if (typeof size === 'string') return size; // Assume it's a Tailwind class
    const sizeMap: { [key: number]: string } = isWidth
      ? {
        192: 'w-48',
        128: 'w-32',
        256: 'w-64',
        320: 'w-80',
        384: 'w-96',
      }
      : {
        144: 'h-36',
        96: 'h-24',
        192: 'h-48',
        240: 'h-60',
        288: 'h-72',
      };
    return sizeMap[size] || (isWidth ? 'w-48' : 'h-36');
  };

  const containerClasses = [
    'relative flex justify-center items-center',
    'relative bg-[#5B5D6B] bg-opacity-40 rounded-lg overflow-hidden flex items-center justify-center',
  ].join(' ');

  return (
    <div className={containerClasses} style={{ aspectRatio: aspectRatio, maxHeight: style?.maxHeight }}>
      <BsPersonFillSlash className="text-5xl mobile:text-3xl text-white mb-1" />
      <div className="absolute mobile:text-s bottom-1 right-1 p-2 rounded text-white text-sm truncate max-w-[70%]">
        {name || 'Trainee'}
      </div>
      <div className="absolute mobile:text-s bottom-1 left-1 p-2 rounded text-yellow-500 text-sm truncate max-w-[70%]">
        Yet to Join
      </div>
    </div>
  );
};

const getAspectRatio = (count: number): string => {
  if (count === 1) return "16 / 9";  // widescreen
  if (count === 2) return "4 / 3";
  if (count === 3) return "4 / 3";   // classic
  return "3 / 2";                    // fallback (≈1.5)
};


const ParticipantExpandedView = ({ view, tableDetails }: Props) => {
  const consumers: any = useSelector(
    (state: RootState) => state.live.settings.consumers
  );
  const [sidebar, setSidebar] = useState({ SessionSidebarWidth: 0, ParticipantSidebarWidth: 0, sidebarOpen: false });
  const profileData = useSelector((state: any) => state?.auth?.user as User);
  const producers = useSelector(
    (state: RootState) => state.live.settings.producers
  );
  const isMobileOrTablet = useMediaQuery("(max-width: 1024px)");
  const { participantPanelWidth, sessionPanelWidth, participantPanelOpen, sessionPlanPanelOpen } = useSelector((state: RootState) => state.countReducer);
  const getTrack = (email: string) => {
    if (!consumers || !producers || !profileData) {
      console.warn("No consumers, producers, or profileData available");
      return { audio: null, video: null };
    }

    if (email === profileData.email) {
      const validProducers = Object.values(producers).filter(
        (producer: any) => producer !== undefined
      );

      const videoProducer: any = validProducers.find(
        (producer: any) => producer?.kind === "video"
      );
      const audioProducer: any = validProducers.find(
        (producer: any) => producer?.kind === "audio"
      );

      return {
        audio: audioProducer
          ? { track: audioProducer.track, locallyPaused: audioProducer.paused }
          : null,
        video: videoProducer
          ? { track: videoProducer.track, locallyPaused: videoProducer.paused }
          : null,
      };
    }

    let audioConsumer = null;
    let videoConsumer = null;

    for (const id in consumers) {
      if (consumers[id].peerId === email && consumers[id].track) {
        if (consumers[id].kind === "audio") {
          audioConsumer = consumers[id];
        } else if (
          consumers[id].kind === "video" &&
          !consumers[id].appData?.share
        ) {
          videoConsumer = consumers[id];
        }
      }
    }

    return { audio: audioConsumer, video: videoConsumer };
  };
  useEffect(() => {
    setSidebar({
      ParticipantSidebarWidth: participantPanelWidth,
      SessionSidebarWidth: sessionPanelWidth,
      sidebarOpen: true,
    });
  }, [participantPanelWidth, sessionPanelWidth,]);

  const totalParticipants = useMemo(() => {
    return tableDetails.reduce((acc, table) => acc + table.seats.length, 0);
  }, [tableDetails]);

  const aspectRatio = getAspectRatio(totalParticipants);
  const columns = isMobileOrTablet ? Math.min(totalParticipants, 2) : Math.min(totalParticipants, 3);
  const rows = Math.ceil(totalParticipants / columns);

  const gridStyle = {
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
  };

  return (
    <div className="mobile:mx-0">
      {tableDetails.map((data: any, index: any) => (
        <>
          <div className=" items-center flex justify-center mx-2 w-full">
            <div className="bg-gray-900 max-w-xs w-full px-4 w-fit py-2 rounded-xl" style={{ borderRadius: "8px", backgroundColor: "#24272C", boxShadow: "-2px -2px 4px 0 rgba(255, 255, 255, 0.25), 2px 2px 4px 0 rgba(0, 0, 0, 0.25)", }}>
              <p className="text-white flex justify-center text-sm">
                {data.table_name}
              </p>
            </div>
            <div className="text-2xl text-white mobile:justify-center flex justify-start m-2 ">
              <div className="bg-gray-600 p-2 rounded-lg opacity-50">
                <AiOutlineFullscreenExit onClick={view} />
              </div>
            </div>
          </div>
          <div className="grid text-white mobile:gap-2  m-2 gap-2" style={gridStyle}>
            {data.seats.map((seat: any, index: any) => {
              const track = getTrack(seat.participant_email);

              const hasStream =
                track &&
                ((track.video && track.video.track) ||
                  (track.audio && track.audio.track));
              return hasStream ? (
                <div className="w-full mobile:w-full h-full mobile:flex mobile:justify-center" key={index}>
                  <ParticipantExpanded
                    key={seat.participant_name}
                    name={seat.participant_name}
                    id={seat.participant_id}
                    // profilePhoto={seat.profilePhoto}
                    size="9"
                    radius="small"
                    streamArray={getTrack(seat.participant_email)}
                    aspectRatio={aspectRatio}
                    video="yes"
                    audio="yes"
                    style={aspectRatio === "16 / 9" ? isMobileOrTablet ? {} : { maxHeight: "100%" } : {}}
                  />
                </div>
              ) : (
                <ExpandedNoParticipantPanel aspectRatio={aspectRatio} style={aspectRatio === "16 / 9" ? isMobileOrTablet ? {} : { maxHeight: "100%" } : {}} key={seat.participant_name} name={seat.participant_name} />
              );
            }
            )}
          </div>
        </>
      ))}

      {/* <div className="flex justify-center">
        {activeAnotherSeat && (
          <button className="bg-orange-600 border-2 border-white rounded-3xl text-white px-2 py-1">
            Leave Group
          </button>
        )}
      </div> */}
    </div>
  );
};
export default ParticipantExpandedView;
